import { remove } from "@src/appV2/api/parsedApi";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics";
import { useMutation, type UseMutationOptions, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { Resources } from "../types/resources";
import { getWorkerDocumentsQueryKey, WORKER_DOCUMENTS_URL } from "./useGetWorkerDocuments";

const deleteWorkerDocumentResponseSchema = z.object({
  data: z.object({
    id: z.string(),
    type: z.literal(Resources.WORKER_DOCUMENTS),
  }),
});

export type DeleteWorkerDocumentResponse = z.infer<typeof deleteWorkerDocumentResponseSchema>;

export interface DeleteWorkerDocumentParams {
  id: string;
}

export function useDeleteWorkerDocument(
  options?: UseMutationOptions<DeleteWorkerDocumentResponse, unknown, DeleteWorkerDocumentParams>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id }: DeleteWorkerDocumentParams) => {
      const response = await remove({
        url: `${WORKER_DOCUMENTS_URL}/${id}`,
        responseSchema: deleteWorkerDocumentResponseSchema,
      });
      return response.data;
    },
    ...options,
    onSuccess: (data, variables, context) => {
      void queryClient.invalidateQueries({ queryKey: getWorkerDocumentsQueryKey() });
      options?.onSuccess?.(data, variables, context);
    },
    meta: {
      ...options?.meta,
      logErrorMessage: APP_V2_APP_EVENTS.DELETE_WORKER_DOCUMENT_FAILURE,
    },
  });
}
